import { computed, effect } from '@preact/signals-react'
import type { ConnectedController } from '../../input/connectedControllers/connectedControllers.types'
import type { ControllerMates, ControllerMatesDeps, ControllerMate } from './controllerMates.types'

/** Plain, controller-derived default name - no hardcoded people, just what device this is. */
function defaultNameForController(controller: ConnectedController): string {
  if (controller.type === 'keyboard') return 'Keyboard'
  return `Controller ${(controller.gamepadIndex ?? 0) + 1}`
}

/**
 * Shared-screen mates: one per connected controller (excluding keyboard), sourced
 * from whichever device profile (see ../deviceProfiles) that controller is currently
 * assigned to. Every controller gets a profile auto-provisioned (see the effect
 * below) the moment it connects, so there's no "needs the picker" gap here - the
 * picker (../../mateList/dialogs/ProfilePickerDialog) is only for manually switching
 * to a different/new profile afterwards.
 */
export function makeControllerMates(deps: ControllerMatesDeps): ControllerMates {
  const { connectedControllers, mode, deviceProfiles } = deps

  // Auto-provision a profile for any connected controller that doesn't have one yet,
  // named after the controller itself (e.g. "Controller 1") - renameable afterwards
  // via the "Me" dialog. One-time per controller id, not a continuous sync.
  effect(() => {
    if (!mode.computedMode.value.sharedScreen) return
    const controllers = connectedControllers.controllers.value.filter((c) => c.type !== 'keyboard')
    for (const c of controllers) {
      if (deviceProfiles.getAssignedProfileId(c.id)) continue
      const created = deviceProfiles.createProfile(defaultNameForController(c))
      deviceProfiles.assignProfile(c.id, created.profileId)
    }
  })

  const mates = computed<ControllerMate[]>(() => {
    const isShared = Boolean(mode.computedMode.value.sharedScreen)
    if (!isShared) return []

    const profilesById = new Map(deviceProfiles.profiles.value.map((p) => [p.profileId, p]))
    const thumbnails = deviceProfiles.thumbnails.value

    // Current behavior: in shared-screen mode, keyboard does not represent a mate.
    const controllers = connectedControllers.controllers.value.filter((c) => c.type !== 'keyboard')

    const resolved: ControllerMate[] = []
    for (const c of controllers) {
      const profileId = deviceProfiles.getAssignedProfileId(c.id)
      const profile = profileId ? profilesById.get(profileId) : undefined
      if (!profile) continue // needs the profile picker

      resolved.push({
        id: c.id,
        controllerId: c.id,
        profileId: profile.profileId,
        name: profile.name,
        thumbnailUrl: thumbnails[profile.profileId],
        controllerType: c.type,
        gamepadIndex: c.gamepadIndex,
        registeredAt: c.registeredAt,
      })
    }
    return resolved
  })

  function start(): void {
    connectedControllers.start()
  }

  function stop(): void {
    connectedControllers.stop()
  }

  function updateMateName(controllerId: string, name: string): void {
    const profileId = deviceProfiles.getAssignedProfileId(controllerId)
    if (!profileId) return
    deviceProfiles.renameProfile(profileId, name)
  }

  function updateMateThumbnail(controllerId: string, thumbnailUrl: string): void {
    const profileId = deviceProfiles.getAssignedProfileId(controllerId)
    if (!profileId) return
    deviceProfiles.setThumbnail(profileId, thumbnailUrl)
  }

  function getMate(controllerId: string): ControllerMate | undefined {
    return mates.value.find((m) => m.controllerId === controllerId)
  }

  return {
    mates: mates as any,
    start,
    stop,
    updateMateName,
    updateMateThumbnail,
    getMate,
  }
}
